import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserPlus, Building2, Mail, Phone, RefreshCw, Check, X, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["tenant_admin", "admin", "manager", "agent", "super_admin"];

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ts; }
}

function statusBadge(s) {
  const map = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rejected: "bg-gray-100 text-gray-600 border-gray-200",
  };
  return <Badge variant="outline" className={map[s] || ""} data-testid={`status-${s}`}>{s}</Badge>;
}

export default function SignupRequests() {
  const [tab, setTab] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [locations, setLocations] = useState([]);

  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  // approve form
  const [tenantMode, setTenantMode] = useState("new"); // new | existing
  const [newTenantName, setNewTenantName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [role, setRole] = useState("tenant_admin");
  const [locationId, setLocationId] = useState("none");
  const [maxLocations, setMaxLocations] = useState(1);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/signup-requests`, { params: { status } });
      setRequests(data.requests || []);
    } catch (e) {
      toast.error("Failed to load signup requests");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  useEffect(() => {
    (async () => {
      try {
        const [t, l] = await Promise.all([
          axios.get(`${API}/admin/tenants`),
          axios.get(`${API}/locations`),
        ]);
        setTenants(t.data.tenants || []);
        setLocations(Array.isArray(l.data) ? l.data : (l.data.locations || []));
      } catch { /* non-blocking */ }
    })();
  }, []);

  const openApprove = (req) => {
    setApproveTarget(req);
    setTenantMode("new");
    setNewTenantName(req.org_name || "");
    setTenantId("");
    setRole("tenant_admin");
    setLocationId("none");
    setMaxLocations(1);
  };

  const submitApprove = async () => {
    if (tenantMode === "new" && !newTenantName.trim()) { toast.error("Enter a tenant name"); return; }
    if (tenantMode === "existing" && !tenantId) { toast.error("Select a tenant"); return; }
    setBusy(true);
    try {
      const body = {
        role,
        max_locations: Number(maxLocations) || 1,
        location_id: locationId === "none" ? null : locationId,
      };
      if (tenantMode === "new") body.new_tenant_name = newTenantName.trim();
      else body.tenant_id = tenantId;
      await axios.post(`${API}/admin/signup-requests/${approveTarget.id}/approve`, body);
      toast.success(`Approved — ${approveTarget.email} can now log in`);
      setApproveTarget(null);
      load(tab);
      window.dispatchEvent(new Event("signup-requests-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally { setBusy(false); }
  };

  const submitReject = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/signup-requests/${rejectTarget.id}/reject`, { reason: rejectReason || null });
      toast.success("Request rejected");
      setRejectTarget(null);
      setRejectReason("");
      load(tab);
      window.dispatchEvent(new Event("signup-requests-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setBusy(false); }
  };

  const tenantLocations = tenantMode === "existing" && tenantId
    ? locations.filter((l) => l.tenant_id === tenantId)
    : locations;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="signup-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <UserPlus className="h-6 w-6 text-cyan-600" /> Signup Requests
          </h1>
          <p className="text-sm text-gray-500 mt-1">Review and approve businesses requesting access to FourPaw IQ.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(tab)} data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="status-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-gray-400">Loading…</CardContent></Card>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center" data-testid="empty-state">
            <Clock className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No {tab === "all" ? "" : tab} signup requests.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <Card key={r.id} data-testid={`request-card-${r.id}`}>
              <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span className="font-semibold text-gray-900">{r.org_name}</span>
                    {statusBadge(r.status)}
                  </div>
                  <div className="text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                    <span>{r.contact_name}</span>
                    <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{r.email}</span>
                    {r.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{r.phone}</span>}
                    <span className="text-gray-400">Requested {fmt(r.created_at)}</span>
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" onClick={() => openApprove(r)} data-testid={`approve-btn-${r.id}`}
                      className="bg-emerald-600 hover:bg-emerald-700">
                      <Check className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejectTarget(r)} data-testid={`reject-btn-${r.id}`}
                      className="border-red-200 text-red-600 hover:bg-red-50">
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Approve dialog */}
      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve {approveTarget?.org_name}</DialogTitle>
            <DialogDescription>Assign a tenant, role and (optionally) a location. The applicant logs in with the password they chose.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Tenant / Organization</Label>
              <RadioGroup value={tenantMode} onValueChange={setTenantMode} className="flex gap-4">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="new" id="tm-new" data-testid="tenant-mode-new" />
                  <Label htmlFor="tm-new" className="font-normal cursor-pointer">Create new</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="existing" id="tm-existing" data-testid="tenant-mode-existing" />
                  <Label htmlFor="tm-existing" className="font-normal cursor-pointer">Existing tenant</Label>
                </div>
              </RadioGroup>
            </div>

            {tenantMode === "new" ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2 col-span-2">
                  <Label className="text-sm">New tenant name</Label>
                  <Input value={newTenantName} onChange={(e) => setNewTenantName(e.target.value)} data-testid="new-tenant-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Max locations</Label>
                  <Input type="number" min={1} value={maxLocations} onChange={(e) => setMaxLocations(e.target.value)} data-testid="max-locations-input" />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-sm">Select tenant</Label>
                <Select value={tenantId} onValueChange={(v) => { setTenantId(v); setLocationId("none"); }}>
                  <SelectTrigger data-testid="tenant-select"><SelectValue placeholder="Choose a tenant" /></SelectTrigger>
                  <SelectContent>
                    {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((rl) => <SelectItem key={rl} value={rl}>{rl.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Location <span className="text-gray-400 font-normal">(optional)</span></Label>
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger data-testid="location-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (assign later)</SelectItem>
                    {tenantLocations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)} data-testid="approve-cancel-btn">Cancel</Button>
            <Button onClick={submitApprove} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-confirm-btn">
              {busy ? "Approving…" : "Approve & provision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {rejectTarget?.org_name}?</DialogTitle>
            <DialogDescription>Optionally include a reason — it will be emailed to the applicant.</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Reason (optional)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} data-testid="reject-cancel-btn">Cancel</Button>
            <Button variant="destructive" onClick={submitReject} disabled={busy} data-testid="reject-confirm-btn">
              {busy ? "Rejecting…" : "Reject request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
